import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowsClockwise, CurrencyCircleDollar, ArrowUp, ArrowDown, ArrowSquareOut, Swap, Ranking, Lightning, Globe
} from "@phosphor-icons/react";

function fmt(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
  if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

function LiquidityGauge({ score }) {
  const color = score >= 70 ? "emerald" : score >= 40 ? "amber" : "red";
  const label = score >= 70 ? "High" : score >= 40 ? "Medium" : "Low";
  return (
    <div className="flex flex-col items-end gap-0.5" data-testid="liquidity-gauge" title={`Liquidity Score: ${score}/100`}>
      <div className="flex items-center gap-1.5">
        <span className={`text-[10px] font-data font-bold text-${color}-400`}>{score}</span>
        <span className={`text-[7px] px-1 py-0 rounded-sm bg-${color}-900/30 text-${color}-400 border border-${color}-800/30`}>{label}</span>
      </div>
      <div className="w-14 bg-zinc-800/60 rounded-full h-1.5 overflow-hidden">
        <div className={`h-full rounded-full bg-${color}-500/70 transition-all`} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>
    </div>
  );
}

function DexRow({ dex, maxVol }) {
  const pct = maxVol > 0 ? (dex.volume_24h / maxVol) * 100 : 0;
  const rankColors = ["text-amber-400", "text-zinc-300", "text-amber-700"];
  const isFeatured = dex.featured;

  return (
    <div
      data-testid={`dex-row-${dex.rank}`}
      className={`px-4 py-3 border-b transition-colors ${
        isFeatured
          ? "border-l-2 border-l-violet-500/60 border-b-zinc-800/40 bg-violet-950/10 hover:bg-violet-950/20"
          : "border-zinc-800/40 hover:bg-zinc-900/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={`text-sm font-data font-bold w-8 text-center flex-shrink-0 ${dex.rank <= 3 ? rankColors[dex.rank - 1] : "text-zinc-600"}`}>
          {dex.rank}
        </span>

        {dex.logo ? (
          <img src={dex.logo} alt={dex.name} className="w-8 h-8 rounded-full flex-shrink-0 bg-zinc-800" loading="lazy" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
            <Swap size={16} className="text-zinc-500" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-zinc-100">{dex.name}</p>
            {isFeatured && (
              <Badge className="text-[7px] px-1.5 py-0 border-0 rounded-sm bg-violet-900/40 text-violet-400 h-3.5">
                TRACKED
              </Badge>
            )}
            {dex.versions && dex.versions.length > 1 && (
              <Badge className="text-[7px] px-1 py-0 border-0 rounded-sm bg-zinc-800 text-zinc-500 h-3.5">
                {dex.versions.length} versions
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {dex.chains?.slice(0, 4).map(c => (
              <span key={c} className="text-[9px] text-zinc-600">{c}</span>
            ))}
            {dex.chain_count > 4 && <span className="text-[9px] text-zinc-600">+{dex.chain_count - 4}</span>}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 mt-2 ml-11 flex-wrap">
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">24h Volume</p>
          <p className="text-xs font-data font-medium text-zinc-100">{fmt(dex.volume_24h)}</p>
          <div className="w-20 bg-zinc-800/40 rounded-sm h-1.5 mt-0.5 overflow-hidden">
            <div className="h-full rounded-sm bg-violet-500/50 transition-all" style={{ width: `${Math.max(pct, 1)}%` }} />
          </div>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">7d Vol</p>
          <p className="text-xs font-data text-zinc-300">{fmt(dex.volume_7d)}</p>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">24h Change</p>
          {dex.change_1d !== null && dex.change_1d !== undefined ? (
            <span className={`text-xs font-data inline-flex items-center gap-0.5 ${dex.change_1d >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {dex.change_1d >= 0 ? <ArrowUp size={10} weight="bold" /> : <ArrowDown size={10} weight="bold" />}
              {dex.change_1d >= 0 ? "+" : ""}{dex.change_1d.toFixed(1)}%
            </span>
          ) : <span className="text-xs text-zinc-600">--</span>}
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Liquidity</p>
          <LiquidityGauge score={dex.liquidity_score || 0} />
        </div>
      </div>
    </div>
  );
}

export default function DexExchanges() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDex = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/exchanges/dex`);
      setData(resp.data);
    } catch (e) { console.error("Failed to fetch DEX:", e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchDex(); }, []);
  const allExchanges = data?.exchanges || [];
  // Show top 10 + any featured that are outside top 10
  const topRanked = allExchanges.filter(e => e.rank <= 10);
  const featuredOutside = allExchanges.filter(e => e.rank > 10 && e.featured);
  const exchanges = [...topRanked, ...featuredOutside];
  const maxVol = exchanges[0]?.volume_24h || 1;

  return (
    <div className="p-5 max-w-[1200px]" data-testid="dex-page">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Swap size={22} weight="bold" className="text-violet-400" />
            <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Top DEX</h1>
          </div>
          <p className="text-xs text-zinc-500 ml-[34px]">Top decentralized exchanges ranked by 24h trading volume</p>
        </div>
        <button data-testid="refresh-dex-btn" onClick={() => fetchDex(true)} disabled={refreshing}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2.5 py-1.5 border border-zinc-800 rounded-sm">
          <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Updating..." : "Refresh"}
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="dex-summary">
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5"><CurrencyCircleDollar size={14} className="text-violet-400" /><span className="text-[9px] uppercase tracking-wider text-zinc-500">Total DEX Vol (Top 10)</span></div>
            <p className="text-lg font-data font-bold text-zinc-100">{fmt(exchanges.reduce((s, e) => s + e.volume_24h, 0))}</p>
          </div>
          {exchanges[0] && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
              <div className="flex items-center gap-2 mb-1.5"><Ranking size={14} className="text-amber-400" /><span className="text-[9px] uppercase tracking-wider text-zinc-500">#1 DEX</span></div>
              <p className="text-lg font-data font-bold text-zinc-100">{exchanges[0].name}</p>
              <p className="text-[10px] text-zinc-500">{fmt(exchanges[0].volume_24h)} / 24h</p>
            </div>
          )}
          {exchanges[1] && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
              <div className="flex items-center gap-2 mb-1.5"><Ranking size={14} className="text-zinc-500" /><span className="text-[9px] uppercase tracking-wider text-zinc-500">#2 DEX</span></div>
              <p className="text-lg font-data font-bold text-zinc-100">{exchanges[1].name}</p>
              <p className="text-[10px] text-zinc-500">{fmt(exchanges[1].volume_24h)} / 24h</p>
            </div>
          )}
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5"><Lightning size={14} className="text-emerald-400" /><span className="text-[9px] uppercase tracking-wider text-zinc-500">Total Protocols</span></div>
            <p className="text-lg font-data font-bold text-zinc-100">{data.total_protocols?.toLocaleString() || "1000+"}</p>
          </div>
        </div>
      )}

      {loading && <div className="space-y-2">{[...Array(10)].map((_, i) => <Skeleton key={i} className="h-14 bg-zinc-900/50" />)}</div>}

      {!loading && exchanges.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid="dex-table">
          <div className="grid grid-cols-[36px_1fr_120px_120px_80px_90px] items-center gap-3 px-4 py-2.5 border-b border-zinc-800/60 bg-zinc-900/50">
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-center">#</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600">Protocol</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">24h Volume</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">7d Volume</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">24h Change</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">Liquidity</span>
          </div>
          {exchanges.map(e => <DexRow key={e.rank} dex={e} maxVol={maxVol} />)}
        </div>
      )}

      {data && (
        <div className="mt-4 pt-3 border-t border-zinc-800/40 flex items-center justify-between">
          <p className="text-[10px] text-zinc-600">Source: DeFiLlama (defillama.com)</p>
          <p className="text-[10px] text-zinc-600">Updated: {new Date(data.fetched_at).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
}
